import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  AssignCardFeedCardsRequest,
  AssignCardFeedCardsResponse,
  CardFeedResponse,
  CreateCardFeedRequest,
} from 'src/queries/cardFeeds/cardFeeds.models'
import { cardFeedsQueryKeys } from 'src/queries/queries.constants'
import { BackendErrorPayload } from 'src/utils/error.utils'

import {
  postAssignCardFeedCards,
  postCreateCardFeed,
} from '../network/cardFeeds.network'
import { OptionalMutationOptions } from './queries.utils'

export type CreateCardFeedVariables = {
  organizationId: string
  body: CreateCardFeedRequest
}

export type CreateCardFeedError = AxiosError<BackendErrorPayload<string>>

export const useCreateCardFeed = (
  options?: OptionalMutationOptions<
    CardFeedResponse,
    CreateCardFeedVariables,
    CreateCardFeedError
  >,
) =>
  useMutation({
    mutationFn: ({ organizationId, body }: CreateCardFeedVariables) =>
      postCreateCardFeed(organizationId, body),
    mutationKey: cardFeedsQueryKeys.create(),
    ...options,
  })

export type AssignCardFeedCardsVariables = {
  cardFeedId: string
  body: AssignCardFeedCardsRequest
}

export type AssignCardFeedCardsError = AxiosError<BackendErrorPayload<string>>

export const useAssignCardFeedCards = (
  options?: OptionalMutationOptions<
    AssignCardFeedCardsResponse,
    AssignCardFeedCardsVariables,
    AssignCardFeedCardsError
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    mutationFn: ({ cardFeedId, body }: AssignCardFeedCardsVariables) =>
      postAssignCardFeedCards(cardFeedId, body),
    mutationKey: cardFeedsQueryKeys.assign(),
    onSuccess: (data, variables, context) => {
      // Assignment flips card status to ASSIGNED; refetch the feed's cards so
      // the table reflects the change without a manual reload.
      void queryClient.invalidateQueries({
        queryKey: cardFeedsQueryKeys.cards(variables.cardFeedId),
      })
      onSuccess?.(data, variables, context)
    },
    ...restOptions,
  })
}
